import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Facebook, Mail, BarChart3, UserPlus, ArrowUpRight, Handshake } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const CURRENT_YEAR = new Date().getFullYear();

export default function Footer() {
  const [logo, setLogo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'logo')
          .maybeSingle();
        
        if (fetchError) {
          console.error('Error fetching logo:', fetchError);
          setError('Unable to load logo');
          return;
        }
        
        setLogo(data?.value || null);
      } catch (err) {
        console.error('Error in logo fetch:', err);
        setError('Unable to load logo');
      }
    };

    fetchLogo();
  }, []);

  // Fallback logo URL in case of error or while loading
  const fallbackLogo = "https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-02.png";

  return (
    <footer className="bg-[#30475e] text-white pt-16 pb-8">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              {error ? (
                <img 
                  src={fallbackLogo}
                  alt="recruteurs.io"
                  className="h-8"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    setError('Logo unavailable');
                  }}
                />
              ) : (
                <img 
                  src={logo || fallbackLogo}
                  alt="recruteurs.io" 
                  className="h-8"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    setError('Logo unavailable');
                  }}
                />
              )}
            </Link>
            <p className="text-neutral-400 mb-6">
              La <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">seule</span> agence RH qui révolutionne le recrutement en combinant <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">expertise</span> RH et <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">technologies</span> innovantes.
            </p>
            <div className="flex space-x-4">
              {[
                { name: 'LinkedIn', icon: <Linkedin size={18} />, url: 'https://linkedin.com/company/recruteurs-io' },
                { name: 'Twitter', icon: <Twitter size={18} />, url: 'https://twitter.com/recruteurs_io' },
                { name: 'Facebook', icon: <Facebook size={18} />, url: 'https://facebook.com/recruteurs.io' },
                { name: 'Email', icon: <Mail size={18} />, url: 'mailto:contact@recruteurs.io' },
              ].map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white hover:text-primary transition-colors"
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
          
          {[
            {
              title: 'Entreprise',
              links: [
                { label: 'Recrutement', url: '/entreprise/recrutement' },
                { label: 'Chasse de dirigeants', url: '/entreprise/recrutement-dirigeants' },
                { label: 'Campagnes', url: '/entreprise/campagnes' },
                { label: 'Externalisation RH', url: '/entreprise/externalisation' },
                { label: 'IA RH sur-mesure', url: '/entreprise/ia' },
                { label: 'Plateforme Parcours Découverte', url: '/decouvrir' },
                { label: 'Cabinet Marseille', url: '/cabinet-recrutement-marseille' },
                { label: 'Cabinet Aix-en-Provence', url: '/cabinet-recrutement-aix-en-provence' },
                { label: 'Cabinet PACA', url: '/cabinet-recrutement-paca' },
                { label: 'Cabinet Poitiers', url: '/cabinet-recrutement-poitiers' },
              ],
            },
            {
              title: 'Candidat',
              links: [
                { label: 'Offres d\'emploi', url: '/offres' },
                { label: 'Découvrir avant de signer', url: '/candidat/decouvrir' },
                { label: 'Pourquoi recruteurs ?', url: '/candidat/pourquoi' },
                { label: 'Coach Talent AI', url: '/candidat/talentai' },
              ],
            },
            {
              title: 'Infos',
              links: [
                { label: 'Questions fréquentes', url: '/infos/questions' },
                { label: 'Charte d\'engagement', url: '/infos/charte' },
                { label: 'Tarifs', url: '/infos/tarifs' },
                { label: 'Contact', url: '/reservation' },
              ],
            },
            {
              title: 'Ressources',
              links: [
                { label: 'Articles RH', url: '/ressources/articles' },
                { label: 'Outils', url: '/ressources/outils' },
                { label: 'Mapping RH (test gratuit)', url: '/ressources/mapping-rh' },
                { label: 'Ressources téléchargeables', url: '/ressources/ressources' },
              ],
            },
          ].map((column) => (
            <div key={column.title}>
              <h4 className="text-lg font-bold mb-4 bg-gradient-to-r from-[#d03484] to-[#d47254] bg-clip-text text-transparent">{column.title}</h4>
              <ul className="space-y-2">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.url}
                      className="text-neutral-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* — Notre écosystème — solutions sœurs + partenaires (cross-link discret) — */}
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-semibold uppercase tracking-widest text-neutral-400">
              Notre écosystème
            </span>
            <span className="flex-1 h-px bg-neutral-700" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                name: 'resoo.fr',
                tagline: 'Cockpit masse salariale retail multi-sites',
                Icon: BarChart3,
                url: 'https://resoo.fr/',
              },
              {
                name: 'loocatme.fr',
                tagline: 'Onboarding automatisé clé en main',
                Icon: UserPlus,
                url: 'https://loocatme.fr/',
              },
              {
                name: 'Thomaz RH',
                tagline: 'Partenaire · cabinet de conseil SIRH (Poitiers)',
                Icon: Handshake,
                url: 'https://thomazrh-cabinetdeconseilsirh.com/',
              },
            ].map((brand) => {
              const Icon = brand.Icon;
              return (
                <a
                  key={brand.name}
                  href={brand.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 hover:ring-white/20 px-4 py-3 transition-all"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-white">
                        {brand.name}
                      </span>
                      <ArrowUpRight className="w-3.5 h-3.5 text-neutral-400 group-hover:text-white transition-colors" />
                    </div>
                    <p className="text-xs text-neutral-400 leading-snug mt-0.5">
                      {brand.tagline}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>

        <hr className="border-neutral-700 my-8" />
        
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-neutral-500 text-sm">
            © {CURRENT_YEAR} recruteurs.io. Tous droits réservés.
          </div>
          <div className="flex space-x-6">
            <Link to="/legal/mentions-legales" className="text-neutral-500 text-sm hover:text-white transition-colors">
              Mentions légales
            </Link>
            <Link to="/legal/confidentialite" className="text-neutral-500 text-sm hover:text-white transition-colors">
              Politique de confidentialité
            </Link>
            <Link to="/legal/cgv" className="text-neutral-500 text-sm hover:text-white transition-colors">
              CGV
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}